import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';

class ShopByFeature extends HTMLElement {
  /** @type {HTMLElement|null} */ #defaultLayer = null;
  /** @type {import('embla-carousel').EmblaCarouselType|null} */ #embla = null;
  /** @type {Map<string, HTMLElement>} */ #layers = new Map();
  /** @type {AbortController|null} */ #controller = null;
  /** @type {MediaQueryList|null} */ #mobileMq = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#relocateImageLayers();

    this.#defaultLayer = this.querySelector('[data-shop-by-feature-image-default]');

    this.querySelectorAll('[data-shop-by-feature-image-layer]').forEach((layer) => {
      const index = layer.getAttribute('data-shop-by-feature-image-layer');
      if (index) this.#layers.set(index, layer);
    });

    const links = [...this.querySelectorAll('[data-shop-by-feature-link]')];

    links.forEach((link) => {
      link.addEventListener('mouseenter', () => this.#activateLink(link, links), { signal });
      link.addEventListener('focus', () => this.#activateLink(link, links), { signal });
    });

    this.#mobileMq = window.matchMedia('(max-width: 1023px)');
    this.#mobileMq.addEventListener('change', () => this.#syncEmbla(), { signal });
    this.#syncEmbla();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#mobileMq = null;
    this.#destroyEmbla();
  }

  #relocateImageLayers() {
    const stack = this.querySelector('[data-shop-by-feature-image-stack]');
    if (!stack) return;

    this.querySelectorAll('.shop-by-feature__image-layer-template').forEach((layer) => {
      layer.classList.remove('hidden', 'shop-by-feature__image-layer-template');
      layer.classList.add(
        'shop-by-feature__image-layer',
        'pointer-events-none',
        'absolute',
        'inset-0',
        'opacity-0',
        'transition-opacity',
        'duration-500'
      );
      layer.removeAttribute('hidden');
      stack.append(layer);
    });
  }

  #syncEmbla() {
    if (this.#mobileMq?.matches) {
      this.#initEmbla();
      return;
    }

    this.#destroyEmbla();
  }

  #initEmbla() {
    if (this.#embla) return;

    const viewport = this.querySelector('[data-shop-by-feature-viewport]');
    if (!viewport) return;

    this.#embla = EmblaCarousel(viewport, {
      align: 'start',
      containScroll: 'trim',
      dragFree: true,
      direction: document.dir || 'ltr',
      breakpoints: {
        1024: {
          active: false,
        },
      },
    });
  }

  #destroyEmbla() {
    if (!this.#embla) return;

    this.#embla.destroy();
    this.#embla = null;
  }

  #isDesktop() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  /**
   * @param {HTMLElement} link
   * @param {HTMLElement[]} links
   */
  #activateLink(link, links) {
    links.forEach((item) => {
      if (item === link) {
        item.setAttribute('data-active', '');
      } else {
        item.removeAttribute('data-active');
      }
    });

    if (this.#isDesktop()) {
      this.#showImageLayer(link.dataset.layerIndex);
    }
  }

  /**
   * @param {string|undefined} layerIndex
   */
  #showImageLayer(layerIndex) {
    const hasLayer = Boolean(layerIndex && this.#layers.has(layerIndex));

    this.#defaultLayer?.classList.toggle('opacity-0', hasLayer);
    this.#defaultLayer?.classList.toggle('opacity-100', !hasLayer);

    this.#layers.forEach((layer, index) => {
      const isActive = layerIndex === index;
      layer.classList.toggle('opacity-100', isActive);
      layer.classList.toggle('opacity-0', !isActive);
    });
  }
}

customElements.define('shop-by-feature', ShopByFeature);
